import React from 'react';
import { Badge } from '../ui/badge.js';
import { cn } from '../../lib/utils.js';

interface TicketStatusBadgeProps {
  status: 'open' | 'in-progress' | 'pending' | 'resolved' | 'closed' | 'escalated';
  className?: string;
  animated?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const TicketStatusBadge: React.FC<TicketStatusBadgeProps> = ({ 
  status, 
  className,
  animated = false,
  size = 'md'
}) => {
  const statusConfig = {
    open: {
      variant: 'default' as const,
      color: 'bg-[#4267A1]/20 text-[#4267A1] border-[#4267A1]/30',
      icon: '🔵',
      label: 'Open'
    },
    'in-progress': {
      variant: 'warning' as const,
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-700/50',
      icon: '🟡',
      label: 'In Progress'
    },
    pending: {
      variant: 'info' as const,
      color: 'bg-orange-500/20 text-orange-400 border-orange-700/50',
      icon: '🟠',
      label: 'Pending'
    },
    resolved: {
      variant: 'success' as const,
      color: 'bg-green-500/20 text-green-400 border-green-700/50',
      icon: '✅',
      label: 'Resolved'
    },
    closed: {
      variant: 'default' as const,
      color: 'bg-slate-800/50 text-slate-300 border-slate-700/50',
      icon: '⚫',
      label: 'Closed'
    },
    escalated: {
      variant: 'danger' as const,
      color: 'bg-red-500/20 text-red-400 border-red-700/50',
      icon: '🔴',
      label: 'Escalated'
    }
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const config = statusConfig[status];
  
  return (
    <Badge 
      variant={config.variant}
      className={`${config.color} ${sizeClasses[size]} font-medium inline-flex items-center gap-1.5 ${animated ? 'animate-pulse' : ''} ${status === 'in-progress' && animated ? 'animate-bounce' : ''} ${className || ''}`}
    >
      <span className="text-xs">{config.icon}</span>
      {config.label}
      {status === 'in-progress' && animated && (
        <div className="flex space-x-0.5">
          <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      )}
    </Badge>
  );
};

export default TicketStatusBadge;
